// Sign-in used while the runtime `signInModes` flag is off (PROD): password + email code.
// Passkeys belong to signInModes, so SignInRoute never enables them here.
import React, { useEffect, useRef, useState } from "react";
import {
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { cognitoClient } from "../aws/cognitoClient";
import "../styles/SignIn.css";
import { useLocation, useNavigate } from "react-router-dom";
import { consumeReturnTo } from "../helpers/returnTo";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../context/AuthContext";
import { getDisplayNameFromToken } from "../helpers/userNameHelper";
import { useTranslation } from "../context/TranslationContext";
import {
  storeRefreshToken,
  requestPasswordRecovery,
  verifyPasswordRecoveryOtp,
  issuePasswordRecoveryTemporaryPassword,
  sendPortalEvent,
  generateLoginAttemptId,
  PORTAL_EVENT_TYPES,
} from "../helpers/apiHelper";
import {
  isPasswordValid,
  getPasswordRules,
  isValidEmail,
} from "../helpers/validationHelper";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Modal from "./Modal";
import SignInHelpForm from "./SignInHelpFormLegacy";  
import { getRememberedEmail, saveRememberedEmail } from "../helpers/rememberedEmailHelper";
import {
  canUsePasskeys,
  getPasskeyAssertion,
  hasPasskeyOptIn,
  isPasskeyCancelledError,
} from "../helpers/passkeys";
import "../styles/Views.css";

const OTP_LENGTH = 6;
const OTP_CODE_ERRORS = new Set(["CodeMismatchException", "ExpiredCodeException"]);
const COGNITO_OTP_VALIDITY_MS = 3 * 60 * 1000;
const RECOVERY_OTP_VALIDITY_MS = 10 * 60 * 1000;
// A WEB_AUTHN challenge kept after a cancelled prompt is reused only while Cognito's session is fresh.
const PASSKEY_CHALLENGE_REUSE_MS = 2 * 60 * 1000;
const PASSKEY_FALLBACK = "PASSWORD_EMAIL_OTP";

const normaliseOtp = (value) => String(value ?? "").replace(/\D/g, "").slice(0, OTP_LENGTH);

const formatCountdown = (remainingMs) => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const getErrorCode = (err) => err?.name || err?.code || "UnknownError";

const SegmentedOtpInput = ({ value, onChange, idPrefix, label }) => {
  const inputRefs = useRef([]);
  const digits = Array.from({ length: OTP_LENGTH }, (_, index) => value[index] || "");

  useEffect(() => {
    const firstEmpty = Array.from(
      { length: OTP_LENGTH },
      (_, index) => value[index] || "",
    ).findIndex((digit) => !digit);
    inputRefs.current[firstEmpty === -1 ? OTP_LENGTH - 1 : firstEmpty]?.focus();
  }, [value]);

  const updateDigits = (nextDigits) => onChange(normaliseOtp(nextDigits.join("")));

  const fillDigits = (index, rawValue) => {
    const incomingDigits = normaliseOtp(rawValue).split("");
    if (!incomingDigits.length) return;

    const nextDigits = [...digits];
    incomingDigits.slice(0, OTP_LENGTH - index).forEach((digit, offset) => {
      nextDigits[index + offset] = digit;
    });
    updateDigits(nextDigits);
    inputRefs.current[Math.min(index + incomingDigits.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleChange = (index, rawValue) => {
    const incomingDigits = normaliseOtp(rawValue);
    if (!incomingDigits) {
      const nextDigits = [...digits];
      nextDigits[index] = "";
      updateDigits(nextDigits);
      return;
    }

    if (incomingDigits.length > 1) {
      fillDigits(index, incomingDigits);
      return;
    }

    const nextDigits = [...digits];
    nextDigits[index] = incomingDigits;
    updateDigits(nextDigits);
    inputRefs.current[Math.min(index + 1, OTP_LENGTH - 1)]?.focus();
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const nextDigits = [...digits];
      if (nextDigits[index]) {
        nextDigits[index] = "";
        updateDigits(nextDigits);
      } else if (index > 0) {
        nextDigits[index - 1] = "";
        updateDigits(nextDigits);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      event.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  return (
    <div className="otp-field">
      <label className="otp-label" htmlFor={`${idPrefix}-0`}>{label}</label>
      <div className="otp-inputs" aria-label={label}>
        {digits.map((digit, index) => (
          <input
            key={`${idPrefix}-${index}`}
            id={`${idPrefix}-${index}`}
            ref={(element) => { inputRefs.current[index] = element; }}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            className="otp-input"
            maxLength={1}
            value={digit}
            aria-label={`${label}: digit ${index + 1}`}
            onInput={(event) => handleChange(index, event.currentTarget.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={(event) => {
              const pastedValue = normaliseOtp(event.clipboardData?.getData("text"));
              if (!pastedValue) return;
              event.preventDefault();
              fillDigits(index, pastedValue);
            }}
          />
        ))}
      </div>
    </div>
  );
};

const SignIn = ({ passkeysEnabled = false }) => {
  const location = useLocation();
  const [email, setEmail] = useState(() => getRememberedEmail());
  const [rememberEmail, setRememberEmail] = useState(() => Boolean(getRememberedEmail()));
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot-password flow: 0 = normal sign-in, 1 = email entry, 2 = OTP entry, 3 = confirm.
  const [passwordResetStep, setPasswordResetStep] = useState(0);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryTransactionId, setRecoveryTransactionId] = useState("");
  const [recoveryOtp, setRecoveryOtp] = useState("");
  const [recoveryToken, setRecoveryToken] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [otpNow, setOtpNow] = useState(Date.now());

  const [resendSuccess, setResendSuccess] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  // { email, response, startedAt }: lets a retry after a cancelled prompt open it straight away.
  const passkeyChallengeRef = useRef(null);

  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { t, language } = useTranslation();

  const loginAttemptIdRef = useRef(null);
  // Blocks a second submit (Enter, click or OTP auto-submit) before the
  // `loading` state has re-rendered the disabled buttons.
  const submittingRef = useRef(false);
  const challengeStartedAtRef = useRef(null);

  const trackEvent = (eventType, { metadata, idToken, email: eventEmail } = {}) => {
    sendPortalEvent(eventType, {
      loginAttemptId: loginAttemptIdRef.current,
      email: idToken ? undefined : eventEmail ?? email,
      idToken,
      metadata: { language, ...metadata },
    });
  };

  const trackChallenge = (response) => {
    challengeStartedAtRef.current = Date.now();
    if (response.ChallengeName === "EMAIL_OTP") {
      trackEvent(PORTAL_EVENT_TYPES.OTP_SENT, {
        metadata: { method: "EMAIL_OTP", challenge: "EMAIL_OTP" },
      });
    } else if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
      trackEvent(PORTAL_EVENT_TYPES.NEW_PASSWORD_REQUIRED, {
        metadata: { challenge: "NEW_PASSWORD_REQUIRED" },
      });
    }
  };

  const challengeAgeSeconds = () =>
    challengeStartedAtRef.current
      ? Math.round((Date.now() - challengeStartedAtRef.current) / 1000)
      : undefined;

  const trackLoginFailed = (step, err, extraMetadata = {}, eventEmail) => {
    trackEvent(PORTAL_EVENT_TYPES.LOGIN_FAILED, {
      email: eventEmail,
      metadata: {
        step,
        errorCode: err ? getErrorCode(err) : undefined,
        errorMessage: err?.message,
        challengeAgeSeconds: challengeAgeSeconds(),
        ...extraMetadata,
      },
    });
  };

  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;

  const passwordRules = getPasswordRules(newPassword);
  const allPasswordRulesMet = isPasswordValid(newPassword);

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const passwordsMatch =
    newPassword.length > 0 && newPassword === confirmNewPassword;

  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isTempPasswordModalOpen, setIsTempPasswordModalOpen] = useState(false);
  const showPasskeyOption =
    canUsePasskeys(passkeysEnabled) && isValidEmail(email) && hasPasskeyOptIn(email);
  const otpRemainingMs = otpExpiresAt ? Math.max(0, otpExpiresAt - otpNow) : 0;
  const otpExpired = otpExpiresAt !== null && otpRemainingMs === 0;

  useEffect(() => {
    if (!otpExpiresAt) return undefined;
    setOtpNow(Date.now());
    const intervalId = window.setInterval(() => setOtpNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [otpExpiresAt]);

  const startOtpTimer = (validityMs) => {
    const now = Date.now();
    setOtpNow(now);
    setOtpExpiresAt(now + validityMs);
  };

  useEffect(() => {
    if (!location.state?.passwordChanged) return;

    setSuccessMessage("signIn.passwordChangedSuccess");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!location.state?.tempPasswordSent) return;

    setIsTempPasswordModalOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const runGuarded = async (handler, ...args) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await handler(...args);
    } finally {
      submittingRef.current = false;
    }
  };

  const submitWith = (handler, canSubmit = true) => (event) => {
    event.preventDefault();
    if (loading || !canSubmit) return;
    runGuarded(handler);
  };

  const handleSignIn = async () => {
    setError("");
    setSuccessMessage("");
    setChallenge(null);

    if (!isValidEmail(email)) {
      setError("signIn.invalidEmail");
      return;
    }

    if (!isPasswordValid(password)) {
      setError("signIn.invalidPassword");
      return;
    }

    loginAttemptIdRef.current = generateLoginAttemptId();
    challengeStartedAtRef.current = null;

    setLoading(true);
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      const response = await cognitoClient.send(command);

      if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        setChallenge(response);
        trackChallenge(response);
      } else if (response.ChallengeName === "EMAIL_OTP") {
        setChallenge(response);
        startOtpTimer(COGNITO_OTP_VALIDITY_MS);
        trackChallenge(response);
      } else if (response.AuthenticationResult) {
        await handleSuccessfulAuth(response.AuthenticationResult);
      } else {
        trackLoginFailed("INITIATE_AUTH", null, {
          errorCode: "UnexpectedResponse",
          challenge: response.ChallengeName,
        });
        setError("signIn.unexpectedResponse");
      }
    } catch (err) {
      trackLoginFailed("INITIATE_AUTH", err);
      setError("signIn.failed");
    } finally {
      setLoading(false);
    }
  };

  const getWebAuthnChallenge = async () => {
    const kept = passkeyChallengeRef.current;
    passkeyChallengeRef.current = null;
    if (
      kept &&
      kept.email === email &&
      Date.now() - kept.startedAt < PASSKEY_CHALLENGE_REUSE_MS
    ) {
      return kept;
    }

    const response = await cognitoClient.send(
      new InitiateAuthCommand({
        AuthFlow: "USER_AUTH",
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PREFERRED_CHALLENGE: "WEB_AUTHN",
        },
      }),
    );
    return { email, response, startedAt: Date.now() };
  };

  const handlePasskeySignIn = async () => {
    setError("");
    setSuccessMessage("");
    setChallenge(null);

    if (!isValidEmail(email)) {
      setError("signIn.invalidEmail");
      return;
    }

    loginAttemptIdRef.current = generateLoginAttemptId();
    const passkeyMetadata = { method: "PASSKEY", fallback: PASSKEY_FALLBACK };
    let step = "PASSKEY_INITIATE";
    let webAuthn = null;

    setLoading(true);
    setPasskeyBusy(true);
    try {
      webAuthn = await getWebAuthnChallenge();
      challengeStartedAtRef.current = webAuthn.startedAt;
      const { response } = webAuthn;

      if (response.ChallengeName !== "WEB_AUTHN") {
        trackLoginFailed(step, null, {
          ...passkeyMetadata,
          errorCode: "UnexpectedResponse",
          challenge: response.ChallengeName,
        });
        setError("passkey.signInFailed");
        return;
      }

      step = "PASSKEY_ASSERTION";
      const credential = await getPasskeyAssertion(
        response.ChallengeParameters?.CREDENTIAL_REQUEST_OPTIONS,
      );

      step = "PASSKEY_VERIFY";
      const verifyResponse = await cognitoClient.send(
        new RespondToAuthChallengeCommand({
          ClientId: clientId,
          ChallengeName: "WEB_AUTHN",
          ChallengeResponses: {
            USERNAME: email,
            CREDENTIAL: credential,
          },
          Session: response.Session,
          ClientMetadata: { loginAttemptId: loginAttemptIdRef.current },
        }),
      );

      if (verifyResponse.AuthenticationResult) {
        await handleSuccessfulAuth(verifyResponse.AuthenticationResult, {
          method: "PASSKEY",
        });
      } else {
        trackLoginFailed(step, null, {
          ...passkeyMetadata,
          errorCode: "UnexpectedResponse",
          challenge: verifyResponse.ChallengeName,
        });
        setError("passkey.signInFailed");
      }
    } catch (err) {
      const cancelled = isPasskeyCancelledError(err);
      if (cancelled && step === "PASSKEY_ASSERTION") {
        // Safari may refuse the prompt after a network call; the next tap reuses this challenge.
        passkeyChallengeRef.current = webAuthn;
      }
      trackLoginFailed(step, err, {
        ...passkeyMetadata,
        cancelled: cancelled || undefined,
      });
      setError(cancelled ? "passkey.signInCancelled" : "passkey.signInFailed");
    } finally {
      setLoading(false);
      setPasskeyBusy(false);
    }
  };

  const handleResendOtp = async () => {
    setError("");
    setOtpCode("");
    setResendSuccess(false);

    if (!isValidEmail(email) || !isPasswordValid(password)) {
      setError("signIn.invalidEmailOrPassword");
      return;
    }

    setLoading(true);
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      const response = await cognitoClient.send(command);

      if (response.ChallengeName === "EMAIL_OTP") {
        setChallenge(response);
        startOtpTimer(COGNITO_OTP_VALIDITY_MS);
        challengeStartedAtRef.current = Date.now();
        trackEvent(PORTAL_EVENT_TYPES.OTP_RESENT, {
          metadata: { method: "EMAIL_OTP", challenge: "EMAIL_OTP" },
        });
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      } else if (response.AuthenticationResult) {
        await handleSuccessfulAuth(response.AuthenticationResult);
      } else {
        trackLoginFailed("RESEND_OTP", null, {
          errorCode: "UnexpectedResponse",
          challenge: response.ChallengeName,
        });
        setError("signIn.unexpectedResendOtpResponse");
      }
    } catch (err) {
      trackLoginFailed("RESEND_OTP", err);
      setError("signIn.resendOtpFailed");
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async () => {
    setError("");

    if (!challenge) {
      setError("signIn.noChallenge");
      return;
    }

    if (!isPasswordValid(newPassword)) {
      setError("signIn.invalidNewPassword");
      return;
    }

    setLoading(true);
    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: clientId,
        ChallengeName: "NEW_PASSWORD_REQUIRED",
        ChallengeResponses: {
          USERNAME: email,
          NEW_PASSWORD: newPassword,
        },
        Session: challenge.Session,
        ...(loginAttemptIdRef.current
          ? { ClientMetadata: { loginAttemptId: loginAttemptIdRef.current } }
          : {}),
      });

      const response = await cognitoClient.send(command);

      if (response.AuthenticationResult || response.ChallengeName === "EMAIL_OTP") {
        trackEvent(PORTAL_EVENT_TYPES.NEW_PASSWORD_SET, {
          metadata: {
            challenge: "NEW_PASSWORD_REQUIRED",
            challengeAgeSeconds: challengeAgeSeconds(),
          },
        });
      }

      if (response.AuthenticationResult) {
        await handleSuccessfulAuth(response.AuthenticationResult);
      } else if (response.ChallengeName === "EMAIL_OTP") {
        setChallenge(response);
        startOtpTimer(COGNITO_OTP_VALIDITY_MS);
        trackChallenge(response);
      } else {
        trackLoginFailed("NEW_PASSWORD", null, {
          errorCode: "UnexpectedResponse",
          challenge: response.ChallengeName,
        });
        setError("signIn.unexpectedNewPasswordResponse");
      }
    } catch (err) {
      trackLoginFailed("NEW_PASSWORD", err, { challenge: "NEW_PASSWORD_REQUIRED" });
      setError("signIn.newPasswordSubmitFailed");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (code = otpCode) => {
    setError("");

    if (otpExpired) {
      setError("signIn.otpExpired");
      return;
    }

    if (!challenge) {
      setError("signIn.noChallenge");
      return;
    }

    if (!code || code.length < 6) {
      setError("signIn.invalidOtpCode");
      return;
    }

    trackEvent(PORTAL_EVENT_TYPES.OTP_SUBMITTED, {
      metadata: {
        method: "EMAIL_OTP",
        challenge: "EMAIL_OTP",
        challengeAgeSeconds: challengeAgeSeconds(),
      },
    });

    setLoading(true);
    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: clientId,
        ChallengeName: "EMAIL_OTP",
        ChallengeResponses: {
          USERNAME: email,
          EMAIL_OTP_CODE: code,
        },
        Session: challenge.Session,
        ...(loginAttemptIdRef.current
          ? { ClientMetadata: { loginAttemptId: loginAttemptIdRef.current } }
          : {}),
      });

      const response = await cognitoClient.send(command);

      if (response.AuthenticationResult) {
        await handleSuccessfulAuth(response.AuthenticationResult);
      } else {
        trackLoginFailed("EMAIL_OTP", null, {
          errorCode: "UnexpectedResponse",
          challenge: response.ChallengeName,
        });
        setError("signIn.otpVerificationFailed");
      }
    } catch (err) {
      trackLoginFailed("EMAIL_OTP", err, { method: "EMAIL_OTP", challenge: "EMAIL_OTP" });
      setError(OTP_CODE_ERRORS.has(err?.name) ? "signIn.otpCodeInvalid" : "signIn.otpSubmitFailed");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulAuth = async (tokens, loginMetadata = {}) => {
    try {
      const decodedToken = jwtDecode(tokens.IdToken);
      const portalUserId = decodedToken["custom:portalUserId"] || null;
      const cocoId = decodedToken["custom:cocoId"] || null;
      const cognitoUserId = decodedToken.sub || null;
      const userData = {
        email,
        username: decodedToken.email || email,
        userId: portalUserId || cognitoUserId || email,
        cocoId,
        portalUserId,
        cognitoUserId,
        authFlow: portalUserId || cognitoUserId ? "portal_user" : "legacy_coco",
        phone_number: decodedToken.phone_number,
        name: getDisplayNameFromToken(decodedToken),
      };

      if (tokens.RefreshToken) {
        await storeRefreshToken(tokens.RefreshToken);
      }

      localStorage.setItem("wasLoggedIn", "true");
      saveRememberedEmail(email, rememberEmail);

      trackEvent(PORTAL_EVENT_TYPES.LOGIN_SUCCESS, {
        idToken: tokens.IdToken,
        metadata: { challengeAgeSeconds: challengeAgeSeconds(), ...loginMetadata },
      });
      loginAttemptIdRef.current = null;
      challengeStartedAtRef.current = null;

      signIn(userData, tokens);
      navigate(consumeReturnTo() || "/");
      resetForm();
    } catch (err) {
      console.error("Error processing successful auth:", err);
      trackLoginFailed("POST_AUTH", err);
      setError("signIn.clientDataFetchFailed");
    }
  };

  const startForgotPassword = () => {
    setError("");
    setSuccessMessage("");
    setRecoveryEmail(email);
    setPasswordResetStep(1);
  };

  const cancelForgotPassword = () => {
    setError("");
    setRecoveryEmail("");
    setRecoveryTransactionId("");
    setRecoveryOtp("");
    setRecoveryToken("");
    setOtpExpiresAt(null);
    setPasswordResetStep(0);
  };

  const handleRequestRecovery = async () => {
    setError("");

    if (!isValidEmail(recoveryEmail)) {
      setError("signIn.invalidEmail");
      return;
    }

    loginAttemptIdRef.current = generateLoginAttemptId();
    const resetMetadata = { flow: "PASSWORD_RESET" };
    trackEvent(PORTAL_EVENT_TYPES.PASSWORD_RESET_REQUESTED, {
      email: recoveryEmail,
      metadata: resetMetadata,
    });

    setLoading(true);
    try {
      const response = await requestPasswordRecovery(recoveryEmail);
      setRecoveryTransactionId(response.transactionId);
      startOtpTimer(RECOVERY_OTP_VALIDITY_MS);
      challengeStartedAtRef.current = Date.now();
      trackEvent(PORTAL_EVENT_TYPES.PASSWORD_RESET_CODE_SENT, {
        email: recoveryEmail,
        metadata: resetMetadata,
      });
      setPasswordResetStep(2);
    } catch (err) {
      trackLoginFailed("PASSWORD_RESET_REQUEST", err, resetMetadata, recoveryEmail);
      setError("signIn.recoveryRequestFailed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRecoveryOtp = async (code = recoveryOtp) => {
    setError("");

    if (otpExpired) {
      setError("signIn.recoveryOtpInvalid");
      return;
    }

    if (!code || code.length < 6) {
      setError("signIn.recoveryOtpInvalid");
      return;
    }

    setLoading(true);
    try {
      const response = await verifyPasswordRecoveryOtp(recoveryTransactionId, code);
      setRecoveryToken(response.recoveryToken);
      setPasswordResetStep(3);
    } catch (err) {
      trackLoginFailed(
        "PASSWORD_RESET_VERIFY",
        err,
        { flow: "PASSWORD_RESET" },
        recoveryEmail,
      );
      setError("signIn.recoveryOtpInvalid");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpCodeChange = (value) => {
    setOtpCode(value);
    if (value.length === OTP_LENGTH && !loading) {
      runGuarded(handleOtpSubmit, value);
    }
  };

  const handleRecoveryOtpChange = (value) => {
    setRecoveryOtp(value);
    if (value.length === OTP_LENGTH && !loading) {
      runGuarded(handleVerifyRecoveryOtp, value);
    }
  };

  const handleIssueTemporaryPassword = async () => {
    setError("");

    setLoading(true);
    try {
      await issuePasswordRecoveryTemporaryPassword(recoveryToken);
      trackEvent(PORTAL_EVENT_TYPES.PASSWORD_RESET_COMPLETED, {
        email: recoveryEmail,
        metadata: { flow: "PASSWORD_RESET", challengeAgeSeconds: challengeAgeSeconds() },
      });
      loginAttemptIdRef.current = null;
      challengeStartedAtRef.current = null;
      cancelForgotPassword();
      navigate(location.pathname, { replace: true, state: { tempPasswordSent: true } });
    } catch (err) {
      trackLoginFailed(
        "PASSWORD_RESET_ISSUE",
        err,
        { flow: "PASSWORD_RESET" },
        recoveryEmail,
      );
      setError("signIn.recoveryIssueFailed");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setConfirmNewPassword("");
    setChallenge(null);
    setNewPassword("");
    setOtpCode("");
    setOtpExpiresAt(null);
  };

  return (
    <div className="signin-container">
      <div className="signin-card">
        {passwordResetStep !== 2 && challenge?.ChallengeName !== "EMAIL_OTP" && (
          <h2>{passwordResetStep === 0 ? t("signIn.title") : t("signIn.passwordRecoveryTitle")}</h2>
        )}
        {error && <p className="error">{t(error)}</p>}
        {successMessage && <p className="success">{t(successMessage)}</p>}

        {!challenge && passwordResetStep === 0 && (
          <form className="signin-form" onSubmit={submitWith(handleSignIn)} noValidate>
            <input
              type="email"
              name="email"
              autoComplete="username"
              placeholder={t("signIn.emailPlaceHolder")}
              aria-label={t("signIn.emailPlaceHolder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="portal-auth-input"
            />

            {showPasskeyOption && (
              <div className="passkey-signin">
                <button
                  type="button"
                  className={`passkey-signin-button ${loading ? "disabled" : ""}`}
                  onClick={() => runGuarded(handlePasskeySignIn)}
                  disabled={loading}
                >
                  {passkeyBusy ? t("passkey.signInWaiting") : t("passkey.signInButton")}
                </button>
                <p className="passkey-signin-divider">{t("passkey.orUsePassword")}</p>
              </div>
            )}

            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                placeholder={t("signIn.passwordPlaceHolder")}
                aria-label={t("signIn.passwordPlaceHolder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="portal-auth-input"
              />

              <span
                className="password-toggle-icon"
                onClick={() => setShowPassword((prev) => !prev)}
                role="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <label className="remember-email">
              <input
                type="checkbox"
                checked={rememberEmail}
                onChange={(e) => setRememberEmail(e.target.checked)}
              />
              <span>{t("signIn.rememberEmail")}</span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className={`${loading ? "disabled" : ""}`}
            >
              {loading ? t("signIn.loading") : t("signIn.signInButton")}
            </button>

            <p
              className="link-button mt-10"
              onClick={() => setIsHelpModalOpen(true)}
            >
              {t("signIn.trouble")}
            </p>

            <p className="link-button" onClick={startForgotPassword}>
              {t("signIn.forgotPasswordLink")}
            </p>

          </form>
        )}

        {passwordResetStep === 1 && (
          <form className="signin-form" onSubmit={submitWith(handleRequestRecovery)} noValidate>
            <p className="mb-10">{t("signIn.recoveryEmailIntro")}</p>
            <input
              type="email"
              name="email"
              autoComplete="email"
              placeholder={t("signIn.emailPlaceHolder")}
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
              className="portal-auth-input"
            />
            <button type="submit" disabled={loading}>
              {loading ? t("signIn.loading") : t("signIn.sendResetCode")}
            </button>
            <p className="link-button mt-10" onClick={cancelForgotPassword}>
              {t("signIn.backToSignIn")}
            </p>
          </form>
        )}

        {passwordResetStep === 2 && (
          <form
            className="otp-screen"
            onSubmit={submitWith(
              handleVerifyRecoveryOtp,
              !otpExpired && recoveryOtp.length === OTP_LENGTH,
            )}
            noValidate
          >
            <h2>{t("signIn.otpTitle")}</h2>
            <p className="otp-destination">
              {t("signIn.otpDestination")} <strong>{recoveryEmail}</strong>.
            </p>
            <p className={`otp-status ${otpExpired ? "otp-status--expired" : ""}`} aria-live="polite">
              {otpExpired
                ? t("signIn.otpExpired")
                : `${t("signIn.otpExpiry")} ${formatCountdown(otpRemainingMs)}.`}
            </p>
            <SegmentedOtpInput
              value={recoveryOtp}
              onChange={handleRecoveryOtpChange}
              idPrefix="recovery-otp"
              label={t("signIn.otpInstruction")}
            />
            <button
              type="submit"
              disabled={loading || otpExpired || recoveryOtp.length !== OTP_LENGTH}
            >
              {loading ? t("signIn.loading") : t("signIn.verificationButton")}
            </button>
            <p className="otp-helper">{t("signIn.recoveryOtpIntro")}</p>
            <button type="button" className="link-button otp-back" onClick={cancelForgotPassword}>
              {t("signIn.otpBack")}
            </button>
          </form>
        )}

        {passwordResetStep === 3 && (
          <form className="signin-form" onSubmit={submitWith(handleIssueTemporaryPassword)} noValidate>
            <button type="submit" disabled={loading}>
              {loading ? t("signIn.loading") : t("signIn.recoverySendTemporaryPasswordButton")}
            </button>
            <p className="link-button mt-10" onClick={cancelForgotPassword}>
              {t("signIn.backToSignIn")}
            </p>
          </form>
        )}

        {challenge?.ChallengeName === "NEW_PASSWORD_REQUIRED" && (
          <form
            className="signin-form"
            onSubmit={submitWith(handleNewPasswordSubmit, allPasswordRulesMet && passwordsMatch)}
            noValidate
          >
            <p className="mb-10">{t("signIn.setNewPasswordMessage")}</p>

            <div className="password-field">
              <input
                type={showNewPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("signIn.newPasswordPlaceholder")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="portal-auth-input"
              />
              <span
                className="password-toggle-icon"
                onClick={() => setShowNewPassword((prev) => !prev)}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div className="password-field">
              <input
                type={showConfirmNewPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("signIn.confirmNewPasswordPlaceholder")}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="portal-auth-input"
              />
              <span
                className="password-toggle-icon"
                onClick={() => setShowConfirmNewPassword((prev) => !prev)}
              >
                {showConfirmNewPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            {confirmNewPassword.length > 0 && (
              <p
                className={`password-match ${
                  passwordsMatch ? "valid" : "invalid"
                }`}
              >
                {passwordsMatch
                  ? t("signIn.passwordsMatch")
                  : t("signIn.passwordsDoNotMatch")}
              </p>
            )}

            <button
              type="submit"
              className="resetPasswordButton"
              disabled={loading || !allPasswordRulesMet || !passwordsMatch}
            >
              {t("signIn.setPasswordButton")}
            </button>

            <PasswordRules passwordRules={passwordRules} t={t} />
          </form>
        )}

        {challenge?.ChallengeName === "EMAIL_OTP" && (
          <form
            className="otp-screen"
            onSubmit={submitWith(handleOtpSubmit, !otpExpired && otpCode.length === OTP_LENGTH)}
            noValidate
          >
            <h2>{t("signIn.otpTitle")}</h2>
            <p className="otp-destination">
              {t("signIn.otpDestination")} <strong>{email}</strong>.
            </p>
            <p className={`otp-status ${otpExpired ? "otp-status--expired" : ""}`} aria-live="polite">
              {otpExpired
                ? t("signIn.otpExpired")
                : `${t("signIn.otpExpiry")} ${formatCountdown(otpRemainingMs)}.`}
            </p>

            <SegmentedOtpInput
              value={otpCode}
              onChange={handleOtpCodeChange}
              idPrefix="sign-in-otp"
              label={t("signIn.otpInstruction")}
            />

            <button
              type="submit"
              disabled={loading || otpExpired || otpCode.length !== OTP_LENGTH}
            >
              {loading ? t("signIn.loading") : t("signIn.verificationButton")}
            </button>

            <p className="otp-helper">{t("signIn.verificationMessage")}</p>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={loading}
              className={`link-button otp-resend ${loading ? "disabled" : ""}`}
            >
              {t("signIn.resendOtpButton")}
            </button>

            {resendSuccess && (
              <p style={{ color: "green", marginTop: "10px" }}>
                {t("signIn.resendOtpSuccess")}
              </p>
            )}
            <button
              type="button"
              className="link-button otp-help"
              onClick={() => setIsHelpModalOpen(true)}
              disabled={loading}
            >
              {t("signIn.otpHelp")}
            </button>
            <button type="button" className="link-button otp-back" onClick={resetForm} disabled={loading}>
              {t("signIn.otpBack")}
            </button>
          </form>
        )}

        <Modal
          isOpen={isHelpModalOpen}
          onClose={() => setIsHelpModalOpen(false)}
        >
          <SignInHelpForm
            initialEmail={email}
            onClose={() => setIsHelpModalOpen(false)}
          />
        </Modal>

        <Modal
          isOpen={isTempPasswordModalOpen}
          onClose={() => setIsTempPasswordModalOpen(false)}
        >
          <div className="temp-password-modal">
            <h2>{t("signIn.tempPasswordModalTitle")}</h2>
            <p className="mb-10">{t("signIn.tempPasswordModalBody")}</p>
            <p className="mb-10">{t("signIn.tempPasswordModalNote")}</p>
            <button onClick={() => setIsTempPasswordModalOpen(false)}>
              {t("signIn.tempPasswordModalOk")}
            </button>
          </div>
        </Modal>

      </div>
    </div>
  );
};

const PasswordRules = ({ passwordRules, t }) => (
  <div className="resetPasswordLines">
    <p className="resetPasswordLineTitle">{t("signIn.newPasswordLineTitle")}</p>
    <p className={`resetPasswordLine ${passwordRules.minLength ? "valid" : ""}`}>{t("signIn.newPasswordLine1")}</p>
    <p className={`resetPasswordLine ${passwordRules.hasNumber ? "valid" : ""}`}>{t("signIn.newPasswordLine2")}</p>
    <p className={`resetPasswordLine ${passwordRules.hasSpecialChar ? "valid" : ""}`}>{t("signIn.newPasswordLine3")}</p>
    <p className={`resetPasswordLine ${passwordRules.hasUppercase ? "valid" : ""}`}>{t("signIn.newPasswordLine4")}</p>
    <p className={`resetPasswordLine ${passwordRules.hasLowercase ? "valid" : ""}`}>{t("signIn.newPasswordLine5")}</p>
  </div>
);

export default SignIn;
